import React, { PropsWithChildren } from 'react';
import theme from './BuyerOptionAccordion.module.scss';
import { Accordion, Dot } from '@ygtechnologies/rubus-ui';
import { classnames, MarkdownDisplay } from '@ygtechnologies/bygr-frontend-component-library';
import { BuyerOptionCardPrice } from '../BuyerOptionCard/BuyerOptionCardPrice';
import { Trans } from '@lingui/react/macro';
import { useOptionConfigurator } from '../OptionConfiguratorContext';
import { ConfiguratorGroupDto, ConfiguratorOptionDto } from '@/services/configurator';
import { ProductThumbnails } from '@/seller/components/products/ProductThumbnails';
import { useImageUrls } from '../useImageUrls';

export interface BuyerOptionAccordionProps extends PropsWithChildren {
    group: ConfiguratorGroupDto;
    /** Fired when the accordion is expanded — used to pre-load this group's configurator visuals. */
    onExpand?: (group: ConfiguratorGroupDto) => void;
}

export const BuyerOptionAccordion: React.FC<BuyerOptionAccordionProps> = ({ children, group, onExpand }) => {
    const { is3d } = useOptionConfigurator();
    const selectedOption = group.selectedOption ?? group.options.find((o) => o.selected) ?? null;

    return (
        <Accordion
            id={String(group.id)}
            className={classnames(theme.accordion, { [theme.accordion3d]: is3d })}
            contentClassName={theme.content}
            onToggle={(isOpen) => isOpen && onExpand?.(group)}
            title={<AccordionTitle group={group} selectedOption={selectedOption} is3d={is3d} />}>
            {group.description && (
                <section className={theme.description}>
                    <MarkdownDisplay markdown={group.description} />
                </section>
            )}
            {children}
        </Accordion>
    );
};

interface AccordionTitleProps {
    group: ConfiguratorGroupDto;
    selectedOption: ConfiguratorOptionDto | null;
    is3d?: boolean;
}

export const AccordionTitle = ({ group, selectedOption, is3d = false }: AccordionTitleProps) => {
    const { getProjectCatalogProductFileUrl, getProjectCatalogProductFileThumbnailUrl } = useImageUrls();
    const defaultOption = group.options.find((o) => o.isDefault) ?? null;
    const imageOption = selectedOption ?? defaultOption;

    return (
        <div className={theme.header}>
            <div className={theme.imageContainer}>
                {!is3d && (
                    <ProductThumbnails
                        imageUrlGenerator={getProjectCatalogProductFileUrl}
                        thumbnailUrlGenerator={getProjectCatalogProductFileThumbnailUrl}
                        images={imageOption?.product?.images || []}
                        className={theme.thumbnail}
                    />
                )}
            </div>

            <div className={theme.groupInfo}>
                <h3>{group.name}</h3>
                {selectedOption ? (
                    <div className={theme.selectedOptionSummary}>
                        <Trans>Selected option</Trans>
                        <div className={theme.nameAndPrice}>{selectedOption.productName}</div>
                    </div>
                ) : (
                    <div className={theme.selectedOptionSummary}>
                        <span className={theme.noneSelected}>
                            <Trans><span className={theme.noneSelectedText}>None selected</span></Trans>
                            <Dot variant="warning" />
                        </span>
                    </div>
                )}
            </div>
            {/* For now we are hiding providedByProfile. If we get feedback, it might come back. */}
            {/* {group.providedByProfile && group.providedByProfile.id > 0 && (
                <div className={theme.providerInfo}>
                    <div className={theme.provider}>
                        <div className={theme.providerDetails}>
                        <Thumbnail
                        className={theme.providerLogo}
                        src={getProfileImageThumbnailUrl(group.providedByProfile?.id ?? 0)}
                        />
                        <span>{group.providedByProfile?.name}</span>
                        </div>
                        </div>
                        </div>
                        )} */}

            <div className={theme.price}>
                {selectedOption && <BuyerOptionCardPrice option={selectedOption} />}
            </div>
        </div>
    );
};
